import React, { useEffect, useMemo, useState } from 'react';
import { Source, Layer, Popup, useMap } from 'react-map-gl/maplibre';
import Table from 'react-bootstrap/Table';

import { useIsAdmin } from '../auth/useIsAdmin';
import { useAdminSession } from '../auth/useAdminSession';
import { useTopLayers } from '../map-gl/useTopLayers';
import {
  ALLUVIAL_SIGNAL_AVAILABLE, ALLUVIAL_SIGNAL_RESTRICTED, ALLUVIAL_SIGNAL_INTERACTIVE,
  ALLUVIAL_SIGNAL_ORDER, ALLUVIAL_SIGNAL_VECTOR_SRC, ALLUVIAL_CLASS_BY_VALUE, LOC_METHOD_LABEL,
  alluvialClassesShown, alluvialRecency, alluvialRecencyCutoff, alluvialSignalLayers,
  buildAlluvialSignalSource, fmtErr, rankPicked,
} from './asxAlluvialStyle';

/** Click tolerance, in screen pixels either side of the cursor. */
const HIT_SLOP_PX = 5;

/**
 * ASX nugget & alluvial signals (admin only) — see asxAlluvialStyle.js.
 *
 * Owns its click handler, like the OCR gold overlay, so switching the layer
 * off takes the handler with it.
 */
export function AsxAlluvialOverlay({ overlayVisibility }) {
  const isAdmin = useIsAdmin();
  // Through the API (a deployed build): no tile request before the session.
  const session = useAdminSession(ALLUVIAL_SIGNAL_RESTRICTED);
  const on = !!overlayVisibility?.asxAlluvial && ALLUVIAL_SIGNAL_AVAILABLE && isAdmin
    && (!ALLUVIAL_SIGNAL_RESTRICTED || session);
  const { current: map } = useMap();
  const [picked, setPicked] = useState(null);
  const source = useMemo(() => (ALLUVIAL_SIGNAL_AVAILABLE ? buildAlluvialSignalSource() : null), []);

  // Reduced to strings before memoising: overlayVisibility is a fresh object
  // on every sidebar change.
  const classKey = alluvialClassesShown(overlayVisibility).join(',');
  const recency = alluvialRecency(overlayVisibility);
  const layers = useMemo(
    () => alluvialSignalLayers(classKey ? classKey.split(',') : [], recency),
    [classKey, recency],
  );

  useEffect(() => {
    if (!map || !on) return undefined;
    const onClick = (e) => {
      const present = ALLUVIAL_SIGNAL_INTERACTIVE.filter((id) => map.getLayer(id));
      if (!present.length) return;
      let hits = map.queryRenderedFeatures(e.point, { layers: present });
      if (!hits.length) {
        hits = map.queryRenderedFeatures([
          [e.point.x - HIT_SLOP_PX, e.point.y - HIT_SLOP_PX],
          [e.point.x + HIT_SLOP_PX, e.point.y + HIT_SLOP_PX],
        ], { layers: present });
      }
      if (!hits.length) return;
      setPicked({ lngLat: e.lngLat, rows: rankPicked(hits.map((h) => h.properties || {})), i: 0 });
    };
    map.on('click', onClick);
    return () => { map.off('click', onClick); };
  }, [map, on]);

  useEffect(() => { if (!on) setPicked(null); }, [on]);
  // A class or recency switched off takes its rows out of an open popup too.
  useEffect(() => {
    if (!picked) return;
    const shown = new Set(classKey.split(','));
    const cutoff = alluvialRecencyCutoff(recency);
    const left = picked.rows.filter((r) => shown.has(r.class)
      && (!cutoff || String(r.recency_date || r.first_reported || '') >= cutoff));
    if (left.length === picked.rows.length) return;
    setPicked(left.length ? { ...picked, rows: left, i: 0 } : null);
  }, [classKey, recency, picked]);

  // Above the OCR gold values (15), below the prospect reports (20).
  useTopLayers('asx-alluvial', 17, ALLUVIAL_SIGNAL_ORDER, map, on);

  if (!on) return null;

  return (
    <>
      <Source id={ALLUVIAL_SIGNAL_VECTOR_SRC} {...source}>
        {layers.map((l) => <Layer key={l.id} {...l} />)}
      </Source>
      {picked && (
        <Popup longitude={picked.lngLat.lng} latitude={picked.lngLat.lat}
               anchor="bottom" closeOnClick={false} maxWidth="360px"
               onClose={() => setPicked(null)}>
          <AlluvialSignalPopup
            p={picked.rows[picked.i]}
            i={picked.i}
            n={picked.rows.length}
            onStep={(d) => setPicked((prev) => ({
              ...prev, i: (prev.i + d + prev.rows.length) % prev.rows.length,
            }))}
          />
        </Popup>
      )}
    </>
  );
}

const has = (v) => v !== undefined && v !== null && v !== '';

/** What was found: the reader's quantity text, then the numbers it parsed. */
export function foundText(p) {
  const nums = [];
  if (has(p.n_nuggets)) nums.push(`${p.n_nuggets} nugget${Number(p.n_nuggets) === 1 ? '' : 's'}`);
  if (has(p.grams_total)) nums.push(`${Number(p.grams_total)} g total`);
  if (has(p.largest_g)) nums.push(`largest ${Number(p.largest_g)} g`);
  const q = has(p.quantity) ? String(p.quantity) : '';
  if (!nums.length) return q || null;
  return q ? `${q} (${nums.join(', ')})` : nums.join(', ');
}

function AlluvialSignalPopup({ p, i, n, onStep }) {
  const cls = ALLUVIAL_CLASS_BY_VALUE[p.class];
  const found = foundText(p);
  const err = fmtErr(p.err_m);
  const company = [p.company, p.ticker && `(ASX:${p.ticker})`].filter(Boolean).join(' ');
  return (
    <div style={{ fontSize: 12 }}>
      {n > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <button type="button" className="btn btn-light btn-sm py-0" onClick={() => onStep(-1)} aria-label="Previous signal">‹</button>
          <span>{i + 1} of {n} signals here</span>
          <button type="button" className="btn btn-light btn-sm py-0" onClick={() => onStep(1)} aria-label="Next signal">›</button>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, marginBottom: 4 }}>
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: cls?.colour || '#868e96', flex: '0 0 auto' }} />
        {cls?.label || p.class}{p.style ? ` · ${String(p.style).replace(/_/g, ' ')}` : ''}
      </div>
      <Table striped bordered size="sm" className="mb-1">
        <tbody>
          <tr><td>Company:</td><td>{company || '—'}</td></tr>
          {has(p.project) && <tr><td>Project:</td><td>{p.project}{has(p.site) ? ` — ${p.site}` : ''}</td></tr>}
          {(!has(p.project) || !has(p.site)) && has(p.places) && p.places !== p.project && (
            <tr><td>Place:</td><td>{p.places}</td></tr>
          )}
          {has(p.tenements) && <tr><td>Tenements:</td><td>{p.tenements}</td></tr>}
          {found && <tr><td>Found:</td><td>{found}{has(p.find_year) ? ` (found ${p.find_year})` : ''}</td></tr>}
          {has(p.finder) && p.finder !== 'unknown' && <tr><td>Finder:</td><td>{p.finder}</td></tr>}
          <tr>
            <td>First reported:</td>
            <td>
              {p.first_reported || '—'}
              {p.class === 'find_repeat' && <span style={{ color: '#6b6b6b' }}> (or earlier: only recaps held)</span>}
            </td>
          </tr>
          {has(p.last_new) && p.last_new !== p.first_reported && (
            <tr><td>Latest new gold:</td><td>{p.last_new}</td></tr>
          )}
          {has(p.latest_mention) && p.latest_mention !== p.first_reported && (
            <tr>
              <td>Latest mention:</td>
              <td>{p.latest_mention}{has(p.n_releases) && <span style={{ color: '#6b6b6b' }}> ({p.n_releases} releases)</span>}</td>
            </tr>
          )}
          <tr>
            <td>Location:</td>
            <td>
              {LOC_METHOD_LABEL[p.loc_method] || p.loc_method || '—'}
              {p.loc_method && <span style={{ color: '#6b6b6b' }}> [{p.loc_method}]</span>}
              {err && <>, ± {err}</>}
              {p.in_own_tenure === false && <span style={{ color: '#b35c00' }}>; not on the company's named tenure</span>}
            </td>
          </tr>
          {has(p.minedex_detect_site) && (
            <tr>
              <td>MINEDEX:</td>
              <td>nearest detecting/dryblowing site {p.minedex_detect_site}
                {has(p.minedex_detect_km) && ` (${Number(p.minedex_detect_km).toFixed(1)} km)`}</td>
            </tr>
          )}
        </tbody>
      </Table>
      {has(p.quote) && (
        <blockquote style={{ margin: '4px 0', padding: '4px 8px', borderLeft: `3px solid ${cls?.colour || '#adb5bd'}`, color: '#333', fontStyle: 'italic' }}>
          “{p.quote}”
        </blockquote>
      )}
      <div>
        {has(p.asx_url) ? (
          <a target="_blank" rel="noreferrer noopener" href={p.asx_url}>
            ASX announcement{has(p.headline) ? `: ${p.headline}` : ''}
          </a>
        ) : (p.headline || null)}
        {has(p.confidence) && <span style={{ color: '#6b6b6b' }}> · {p.confidence} confidence</span>}
      </div>
      <div style={{ color: '#6b6b6b', fontSize: 10, marginTop: 2 }}>
        Derived from the company's ASX announcement (company copyright); admin research use only.
      </div>
    </div>
  );
}
